import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import { FormFieldValue } from '../forms/FormFieldValue';
import { type ProductBulkPricingMarkupLineItem, validate as validateProductBulkPricingMarkupLineItem } from '../../models/ProductBulkPricingMarkupLineItem';
import NumberField from '../NumberField';
import { useCallback, useState } from 'react';

export interface ProductBulkPricingMarkupLineItemFormState {
  values: Partial<Omit<ProductBulkPricingMarkupLineItem, 'id'>>;
  errors: Partial<Record<keyof ProductBulkPricingMarkupLineItemFormState['values'], string>>;
}

export interface ProductBulkPricingMarkupLineItemFormProps {
  initialValue?: ProductBulkPricingMarkupLineItem;
  initialErrors?: Partial<Record<keyof ProductBulkPricingMarkupLineItemFormState['values'], string>>;
  onReset?: (formValues: Partial<ProductBulkPricingMarkupLineItemFormState['values']>) => void;
  onFormValuesUpdate?: (formValues: Partial<ProductBulkPricingMarkupLineItemFormState['values']>) => void;
}

export const newProductBulkPricingMarkupLineItem = {
  id: 0,
  productId: 0,
  pounds: 0,
  markup: 0
} as ProductBulkPricingMarkupLineItem;

export default function ProductBulkPricingMarkupLineItemForm(props: ProductBulkPricingMarkupLineItemFormProps) {
  const {
    initialValue,
    initialErrors = {},
    onReset = () => { },
    onFormValuesUpdate = () => { }
  } = props;

  const [errors, setErrors] = useState<Partial<Record<keyof ProductBulkPricingMarkupLineItemFormState['values'], string>>>({});

  const handleFormFieldChange = useCallback(
    (name: keyof ProductBulkPricingMarkupLineItemFormState['values'], value: FormFieldValue) => {
      const validateField = (values: Partial<ProductBulkPricingMarkupLineItemFormState['values']>) => {
        const { issues } = validateProductBulkPricingMarkupLineItem(values);

        const issue = issues?.find((issue) => issue.path?.[0] === name);

        if (issue) {
          setErrors({
            ...errors,
            [name]: issue.message,
          });
          return false;
        }

        return true;
      };

      const newFormValues = { ...initialValue, [name]: value };

      const valid = validateField(newFormValues);

      if (valid) {
        onFormValuesUpdate(newFormValues);
      }
    },
    [initialValue, errors, onFormValuesUpdate],
  );

  const handleNumberFieldChange = useCallback(
    (value: number | null, fieldName: string) => {
      handleFormFieldChange(
        fieldName as keyof ProductBulkPricingMarkupLineItemFormState['values'],
        value,
      );
    },
    [handleFormFieldChange],
  );

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset(initialValue || newProductBulkPricingMarkupLineItem);
    }
  }, [initialValue, onReset]);

  return (
    <Box
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 8, sm: 4 }} sx={{ display: 'flex' }}>
            <NumberField
              required
              value={initialValue?.pounds || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'pounds')}
              name="pounds"
              label="Pounds"
              error={!!errors.pounds || !!initialErrors.pounds}
              helperText={(errors.pounds || initialErrors.pounds) ?? ''}
              weightFieldUnits="lbs"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 8, sm: 4 }} sx={{ display: 'flex' }}>
            <NumberField
              required
              value={initialValue?.markup || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'markup')}
              name="markup"
              label="Markup"
              error={!!errors.markup || !!initialErrors.markup}
              helperText={(errors.markup || initialErrors.markup) ?? ''}
              endAdornmentText="%"
              fullWidth
            />
          </Grid>
        </Grid>
      </FormGroup>
    </Box>
  );
}
